import { Inject, Injectable, Optional } from '@angular/core';

import { AnalyticsSegmentClient } from './analytics-segment-client';
import {
  AnalyticsConfig,
  AnalyticsProps,
  AnalyticsViewContext,
  ANALYTICS_VIEW_CONTEXT,
  SegmentAPIMethods,
  SegmentClientOptions,
} from './analytics.types';

const throwNoContextError = () => {
  const msg = `[Analytics] Missing Context,
    "requireViewContext" is set to "true" but no "ANALYTICS_CONTEXT" found.
    https://github.com/Recruitee/recruitee/blob/master/libs/analytics/README.md#context
    
    
  `;

  throw new Error(msg);
};

const throwReservedContextPropertyError = () => {
  const msg = `[Analytics] "context" property misused
    "viewContext" is reserved property, it should be provided as Injection token.

    https://github.com/Recruitee/recruitee/blob/master/libs/analytics/README.md#context
    
    
  `;

  throw new Error(msg);
};

@Injectable()
export abstract class AnalyticsBase implements SegmentAPIMethods {
  public abstract readonly namespace: string;
  public abstract readonly requireViewContext: boolean;
  public defaultTrackOptions: SegmentClientOptions = {
    integrations: {
      All: true,
      Intercom: false,
    },
  };

  constructor(
    private segmentClient: AnalyticsSegmentClient,
    private config: AnalyticsConfig,
    @Optional() @Inject(ANALYTICS_VIEW_CONTEXT) private viewContext: AnalyticsViewContext[],
  ) {}

  public track(
    evtName: string,
    evtProps?: AnalyticsProps,
    options: SegmentClientOptions = this.defaultTrackOptions,
  ) {
    const props = this.requireViewContext ? this.withContext(evtProps) : evtProps;

    if (this.requireViewContext && props === undefined) {
      return;
    }

    if (this.namespace !== '') {
      this.segmentClient.track(`[${this.namespace}] ${evtName}`, props, options);
    } else {
      this.segmentClient.track(evtName, props, options);
    }
  }

  public alias(id: number): void {
    this.segmentClient.alias(id);
  }

  public identify(
    userId?: number,
    traits?: Record<string, string | number>,
    options?: SegmentClientOptions,
  ): void {
    this.segmentClient.identify(userId, traits, options);
  }

  public page(
    category: string,
    name: string,
    properties?: any,
    options?: SegmentClientOptions,
  ): void {
    this.segmentClient.page(category, name, properties, options);
  }

  public reset() {
    this.segmentClient.reset();
  }

  private withContext(evtProps: AnalyticsProps = {}) {
    if (!Array.isArray(this.viewContext) || this.viewContext.length === 0) {
      if (this.config?.isDebug) {
        throwNoContextError();
      } else {
        return;
      }
    }

    if (evtProps && evtProps.viewContext) {
      if (this.config?.isDebug) {
        throwReservedContextPropertyError();
      } else {
        return;
      }
    }

    return {
      ...evtProps,
      viewContext: this.viewContext,
    };
  }
}
